import { useState, useEffect } from 'react';
import { apiFetch } from '../../../utils/api';

/**
 * useProjectData - Custom hook for managing project data fetching and state
 * Uses 2-step hierarchical approach: project -> families (with nested rulesets + features)
 * @returns {Object} - Project data and functions
 */
export function useProjectData() {
    const [projects, setProjects] = useState([]);
    const [families, setFamilies] = useState([]);
    const [isLoading, setIsLoading] = useState(true);

    const fetchFamilies = async (projectId) => {
        try {
            // Use hierarchical endpoint with includeFeatures
            const url = projectId ? `/api/products?projectId=${projectId}&includeFeatures=true` : '/api/products';
            const res = await apiFetch(url);
            if (res.ok) {
                const data = await res.json();
                setFamilies(data);
                return data;
            }
            return [];
        } catch (err) {
            console.error('Failed to fetch families', err);
            return [];
        }
    };

    const fetchProjects = async () => {
        setIsLoading(true);
        try {
            const res = await apiFetch('/api/projects');
            if (res.ok) {
                const data = await res.json();
                setProjects(data);
                return data;
            }
        } catch (error) {
            console.error('Failed to fetch projects', error);
        } finally {
            setIsLoading(false);
        }
        return [];
    };

    const fetchProjectDetails = async (project) => {
        try {
            // Step 1: Get project details
            const projResRaw = await apiFetch(`/api/projects/${project.id}`);
            const projRes = projResRaw.ok ? await projResRaw.json() : { Project: null, LinkedFamilies: [] };
            
            // Step 2: Get families with nested rulesets + features (replaces /api/rulesets and /api/init calls)
            const famResRaw = await apiFetch(`/api/products?projectId=${project.id}&includeFeatures=true`);
            const famRes = famResRaw.ok ? await famResRaw.json() : [];

            const linkedFamilyIds = projRes.linkedFamilies || projRes.LinkedFamilies || [];
            const familyLinks = projRes.familyLinks || projRes.FamilyLinks || [];
            const projectData = projRes.Project || projRes.project || project;
            
            // Extract and deduplicate rulesets and features from families
            const projectFamilies = Array.isArray(famRes) ? famRes : [];
            
            // Build maps to track source family IDs for rulesets and features
            const rulesetsMap = new Map();
            const featuresMap = new Map();
            
            projectFamilies.forEach(f => {
                // Track ruleset-family relationships
                (f.ruleSets || []).forEach(rs => {
                    if (!rulesetsMap.has(rs.id)) {
                        rulesetsMap.set(rs.id, { ...rs, sourceFamilyIds: [] });
                    }
                    const existing = rulesetsMap.get(rs.id);
                    if (!existing.sourceFamilyIds.includes(f.id)) {
                        existing.sourceFamilyIds.push(f.id);
                    }
                });
                
                // Track feature-family relationships
                (f.features || []).forEach(feat => {
                    if (!featuresMap.has(feat.id)) {
                        featuresMap.set(feat.id, { ...feat, sourceFamilyIds: [] });
                    }
                    const existing = featuresMap.get(feat.id);
                    if (!existing.sourceFamilyIds.includes(f.id)) {
                        existing.sourceFamilyIds.push(f.id);
                    }
                });
            });
            
            const projectRuleSets = Array.from(rulesetsMap.values());
            const projectFeatures = Array.from(featuresMap.values());

            return {
                ...project,
                ...(projectData && {
                    allowedOrigins: projectData.allowedOrigins,
                    expiresAt: projectData.expiresAt
                }),
                linkedFamilies: linkedFamilyIds,
                familyLinks,
                families: projectFamilies,
                rulesets: projectRuleSets,
                features: projectFeatures,
                isLoadingLinks: false
            };
        } catch (error) {
            console.error('Failed to fetch project details', error);
            return { ...project, isLoadingLinks: false };
        }
    };

    return {
        projects,
        families,
        isLoading,
        setProjects,
        setFamilies,
        setIsLoading,
        fetchFamilies,
        fetchProjects,
        fetchProjectDetails
    };
}